import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Award, Download, CheckCircle, Lock, Loader2, GraduationCap, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LearnerCertificatsPage = () => {
  const { t } = useLanguage();
  const [certificates, setCertificates] = useState([]);
  const [eligibility, setEligibility] = useState({});
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);
  const [resending, setResending] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [certsRes, coursesRes] = await Promise.all([
        fetch(`${API}/certificates/my-certificates`, { credentials: 'include' }),
        fetch(`${API}/courses/my-courses`, { credentials: 'include' })
      ]);
      if (certsRes.ok) setCertificates(await certsRes.json());
      if (coursesRes.ok) {
        const coursesData = await coursesRes.json();
        setCourses(coursesData);
        // Check eligibility for each course
        const eligMap = {};
        for (const course of coursesData) {
          try {
            const checkRes = await fetch(`${API}/certificates/check/${course.course_id}`, {
              method: 'POST', credentials: 'include'
            });
            if (checkRes.ok) eligMap[course.course_id] = await checkRes.json();
          } catch (e) { /* skip */ }
        }
        setEligibility(eligMap);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleGenerate = async (courseId) => {
    setGenerating(courseId);
    try {
      const res = await fetch(`${API}/certificates/generate/${courseId}`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        const cert = await res.json();
        setCertificates(prev => [...prev, cert]);
        setEligibility(prev => ({ ...prev, [courseId]: { ...prev[courseId], already_issued: true, certificate: cert } }));
        toast.success(t('cert_generated'));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error');
      }
    } catch (e) { toast.error('Connection error'); }
    finally { setGenerating(null); }
  };

  const handleDownload = (certificateId) => {
    window.open(`${API}/certificates/${certificateId}/pdf`, '_blank');
  };

  const handleResendEmail = async (cert) => {
    setResending(cert.certificate_id);
    try {
      const res = await fetch(`${API}/certificates/${cert.certificate_id}/resend-email`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        toast.success(t('cert_resent'));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error');
      }
    } catch (e) { toast.error('Connection error'); }
    finally { setResending(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500" /></div>;

  return (
    <div className="space-y-6" data-testid="certificats-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Award className="h-6 w-6 text-teal-500" />
          {t('cert_title')}
        </h1>
        <p className="text-slate-500">{t('cert_subtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center"><GraduationCap className="h-6 w-6 text-teal-600" /></div>
            <div><p className="text-2xl font-bold text-slate-900">{courses.length}</p><p className="text-sm text-slate-500">Formations inscrites</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center"><Award className="h-6 w-6 text-emerald-600" /></div>
            <div><p className="text-2xl font-bold text-emerald-600">{certificates.length}</p><p className="text-sm text-slate-500">Certificats obtenus</p></div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center"><CheckCircle className="h-6 w-6 text-blue-600" /></div>
            <div><p className="text-2xl font-bold text-blue-600">{Object.values(eligibility).filter(e => e.eligible && !e.already_issued).length}</p><p className="text-sm text-slate-500">Certificats disponibles</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Issued certificates */}
      {certificates.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Certificats obtenus</h2>
          <div className="grid gap-4">
            {certificates.map(cert => (
              <Card key={cert.certificate_id} className="border-l-4 border-l-emerald-500" data-testid={`cert-${cert.certificate_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center">
                        <Award className="h-7 w-7 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{cert.course_title}</p>
                        <p className="text-sm text-slate-500">N° {cert.certificate_number}</p>
                        <p className="text-xs text-slate-400">Délivré le {new Date(cert.issued_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="border-teal-300 text-teal-600 hover:bg-teal-50"
                        onClick={() => handleResendEmail(cert)}
                        disabled={resending === cert.certificate_id}
                        data-testid={`resend-cert-${cert.certificate_id}`}
                      >
                        {resending === cert.certificate_id ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4 mr-2" />
                        )}
                        Envoyer par email
                      </Button>
                      <Button
                        className="bg-teal-500 hover:bg-teal-600 text-white"
                        onClick={() => handleDownload(cert.certificate_id)}
                        data-testid={`download-cert-${cert.certificate_id}`}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t('cert_download')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Course progress towards certificates */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">Progression vers les certificats</h2>
        <div className="grid gap-4">
          {courses.map(course => {
            const elig = eligibility[course.course_id];
            const hasCert = certificates.some(c => c.course_id === course.course_id);
            
            return (
              <Card key={course.course_id} className={hasCert ? 'opacity-60' : ''} data-testid={`course-cert-${course.course_id}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        hasCert ? 'bg-emerald-100' : elig?.eligible ? 'bg-teal-100' : 'bg-slate-100'
                      }`}>
                        {hasCert ? <CheckCircle className="h-6 w-6 text-emerald-500" /> :
                         elig?.eligible ? <Award className="h-6 w-6 text-teal-500" /> :
                         <Lock className="h-6 w-6 text-slate-400" />}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{course.title}</p>
                        {elig && !hasCert && (
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-slate-500">
                              Modules : {elig.modules_completed}/{elig.modules_total}
                            </span>
                            <span className="text-xs text-slate-500">
                              Évaluations : {elig.evaluations_completed}/{elig.evaluations_total}
                            </span>
                          </div>
                        )}
                        {hasCert && <p className="text-xs text-emerald-600">Certificat obtenu</p>}
                      </div>
                    </div>
                    <div>
                      {hasCert ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Obtenu</Badge>
                      ) : elig?.eligible ? (
                        <Button
                          className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                          onClick={() => handleGenerate(course.course_id)}
                          disabled={generating === course.course_id}
                          data-testid={`generate-cert-${course.course_id}`}
                        >
                          {generating === course.course_id ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Génération...</>
                          ) : (
                            <><Award className="h-4 w-4 mr-2" />Obtenir le certificat</>
                          )}
                        </Button>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">
                          <Lock className="h-3 w-3 mr-1" />
                          En cours
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LearnerCertificatsPage;
